import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Room, RoomEvent, Track, ConnectionQuality, VideoPresets, DisconnectReason,
} from 'livekit-client';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { getConversationName } from '../../utils/chatHelpers';
import CallExtras from './CallExtras';
import MeetingNotesPanel from './MeetingNotesPanel';
import { useCallKeepAlive, IS_TOUCH_DEVICE, LOCK_HINT } from '../../hooks/useCallKeepAlive';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff, Maximize2, Minimize2, Monitor, Minus, Phone,
  UserPlus, X, Wifi, WifiOff, SignalHigh, SignalMedium, SignalLow, Volume2, Server, SwitchCamera, Hand, NotebookPen, EyeOff, Eye,
} from 'lucide-react';
import { confirmDialog } from '../../hooks/useConfirm';

const API = process.env.REACT_APP_BACKEND_URL;
const DATA_TOPIC = 'bbc'; // raise-hand / lower-all messages shared with the native Android call screen
const enc = (o) => new TextEncoder().encode(JSON.stringify(o));

const QUALITY_UI = {
  excellent: { icon: SignalHigh, color: 'text-green-400', label: 'Excellent' },
  good: { icon: SignalMedium, color: 'text-green-300', label: 'Good' },
  poor: { icon: SignalLow, color: 'text-yellow-400', label: 'Poor' },
  lost: { icon: WifiOff, color: 'text-red-400', label: 'Lost' },
  checking: { icon: Wifi, color: 'text-gray-400', label: 'Checking...' },
};

const fmt = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

// LiveKit SFU call: one uplink per participant regardless of room size; the
// server forwards tracks (simulcast + adaptive stream keep 20+ people smooth).
export default function LiveKitCall() {
  const chat = useChat();
  const auth = useAuth();
  const { callState, endCall, registerHandler, markConnected, inviteToCall, setMediaMode } = chat;
  const myId = (auth && auth.user && auth.user.user_id) || '';
  const token = auth && auth.token;
  const { callId, phase } = callState;
  const callType = callState.callType || 'video';
  const participants = callState.participants || [];
  // Who we are calling (1:1 → other person's name, group → group name) for the "Calling …" screen
  const conv = (chat.conversations || []).find((c) => c.conversation_id === callState.conversationId);
  const recipientName = conv ? getConversationName(conv, auth.user && auth.user.user_id) : (participants.find((p) => p.user_id !== (auth.user && auth.user.user_id)) || {}).name;

  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(callType === 'video');
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const aloneTimerRef = useRef(null);
  const connectedOnceRef = useRef(false);
  const callStateRef = useRef(callState); callStateRef.current = callState;
  const [callDuration, setCallDuration] = useState(0);
  const [roomState, setRoomState] = useState('connecting'); // connecting | joined | reconnecting | failed
  const [tiles, setTiles] = useState({}); // identity -> {name, video, audio, screen, camMuted, micMuted, hand, speaking}
  const [handRaised, setHandRaised] = useState(false);
  const [meSpeaking, setMeSpeaking] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [aiOn, setAiOn] = useState(false);
  const handRaisedRef = useRef(false);
  const [quality, setQuality] = useState('checking');
  const [stats, setStats] = useState({}); // identity|'me' -> { kbps, loss }
  const prevBytesRef = useRef({});
  const statsAccRef = useRef({ kbps: 0, loss: 0, n: 0 });
  useEffect(() => {
    const t = setInterval(async () => {
      const room = roomRef.current; if (!room) return;
      const next = {};
      const readTracks = async (key, pubs, dir) => {
        let bytes = 0, lost = 0, recv = 0;
        for (const pub of pubs) {
          const tr = pub.track; if (!tr || !tr.getRTCStatsReport) continue;
          try {
            const rep = await tr.getRTCStatsReport(); if (!rep) continue;
            rep.forEach((r) => {
              if (dir === 'out' && r.type === 'outbound-rtp') bytes += r.bytesSent || 0;
              if (dir === 'out' && r.type === 'remote-inbound-rtp') { lost += r.packetsLost || 0; recv += (r.packetsReceived || 0); }
              if (dir === 'in' && r.type === 'inbound-rtp') { bytes += r.bytesReceived || 0; lost += r.packetsLost || 0; recv += r.packetsReceived || 0; }
            });
          } catch (e) { /* noop */ }
        }
        const prev = prevBytesRef.current[key] || { bytes, lost, recv };
        const kbps = Math.max(0, Math.round(((bytes - prev.bytes) * 8) / 1000 / 3));
        const dl = lost - prev.lost, dr = recv - prev.recv;
        const loss = dr + dl > 0 ? Math.round((dl / (dr + dl)) * 1000) / 10 : 0;
        prevBytesRef.current[key] = { bytes, lost, recv };
        next[key] = { kbps, loss };
      };
      await readTracks('me', Array.from(room.localParticipant.trackPublications.values()), 'out');
      if (next.me && next.me.kbps > 0) { const a = statsAccRef.current; a.kbps += next.me.kbps; a.loss += next.me.loss; a.n += 1; }
      // Poor-connection hint: loss > 5 % on 3+ consecutive 3-second samples (≈10 s), at most once a minute
      const pr = poorRef.current; const worst = Math.max(next.me ? next.me.loss || 0 : 0, ...Object.values(next).map((q) => (q && q.loss) || 0));
      pr.streak = worst > 5 ? pr.streak + 1 : 0;
      if (pr.streak >= 3 && Date.now() - pr.lastToast > 60000) { pr.lastToast = Date.now(); toast.warning('Poor connection — audio/video may break up. Move closer to Wi-Fi or a better signal.', { duration: 6000 }); }
      for (const p of room.remoteParticipants.values()) await readTracks(p.identity, Array.from(p.trackPublications.values()), 'in');
      setStats(next);
    }, 3000);
    return () => clearInterval(t);
  }, []);
  const fmtStats = (st) => (st ? `${st.kbps} kbps · ${st.loss}% loss` : '…');
  const [showQualityDetails, setShowQualityDetails] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [needsAudioTap, setNeedsAudioTap] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [pillPos, setPillPos] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem('bbc:callPillPos') || 'null'); if (p && typeof p.x === 'number') return p; } catch (e) { /* ignore */ }
    return null;
  });

  const roomRef = useRef(null);
  const localVideoRef = useRef(null);
  const containerRef = useRef(null);
  const connectingRef = useRef(false);
  const pillDragRef = useRef({ active: false, moved: false, offX: 0, offY: 0, w: 180, h: 56 });

  const remoteCount = Object.keys(tiles).length;
  const connectionState = remoteCount > 0 && roomState !== 'failed' ? 'connected' : roomState === 'failed' ? 'failed' : 'connecting';

  const nameFor = useCallback((p) => p.name || (participants.find((x) => x.user_id === p.identity) || {}).name || 'Participant', [participants]);

  const patchTile = useCallback((identity, patch) => {
    setTiles((prev) => ({ ...prev, [identity]: { ...(prev[identity] || { name: '', video: null, audio: null, screen: null, camMuted: true, speaking: false }), ...patch } }));
  }, []);

  const attachLocalPreview = useCallback((room) => {
    const el = localVideoRef.current; if (!el || !room) return;
    const screen = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    const cam = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const pub = (screen && screen.track) ? screen : cam;
    if (pub && pub.track) { pub.track.attach(el); el.style.transform = pub.source === Track.Source.ScreenShare ? 'none' : 'scaleX(-1)'; }
  }, []);

  // ==================== CONNECT ====================
  // BBC Connect: inside the Android app (v2.3.0+) the media runs in the native LiveKit screen
  // (screen share, lock-screen audio). The web page keeps owning ring/accept/end signalling.
  const nativeBridge = window.AndroidBridge && typeof window.AndroidBridge.openNativeCall === 'function' ? window.AndroidBridge : null;
  const [nativeMode, setNativeMode] = useState(false);
  // The stage (video area) sits exactly between the top and bottom control bars: measure them so nothing overlaps the video.
  const topBarRef = useRef(null);
  const bottomBarRef = useRef(null);
  const [barPad, setBarPad] = useState({ top: 56, bottom: 96 });
  const [controlsHidden, setControlsHidden] = useState(false); // full-scene: hide top + bottom bars (shared screen fills the stage)
  useEffect(() => {
    const measure = () => setBarPad(controlsHidden ? { top: 0, bottom: 0 } : { top: topBarRef.current?.offsetHeight || 56, bottom: bottomBarRef.current?.offsetHeight || 96 });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) { if (topBarRef.current) ro.observe(topBarRef.current); if (bottomBarRef.current) ro.observe(bottomBarRef.current); }
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  }, [minimized, nativeMode, isVideoEnabled, isScreenSharing, callType, controlsHidden]);
  const [bubbleMuted, setBubbleMuted] = useState(false);
  // Draggable bubble: position is kept while the call lasts; a drag must not count as a tap on the buttons
  const [bubblePos, setBubblePos] = useState(() => { try { const v = JSON.parse(localStorage.getItem('bbc_call_bubble_pos') || 'null'); return v && v.x >= 0 && v.y >= 0 && v.x < window.innerWidth - 40 && v.y < window.innerHeight - 40 ? v : null; } catch (e) { return null; } });
  useEffect(() => { if (bubblePos) try { localStorage.setItem('bbc_call_bubble_pos', JSON.stringify(bubblePos)); } catch (e) { /* noop */ } }, [bubblePos]);
  const poorRef = useRef({ streak: 0, lastToast: 0 });
  const bubbleDrag = useRef(null);
  const bubbleJustMoved = useRef(false);
  const [nativeRetry, setNativeRetry] = useState(0);
  const nativeFailedRef = useRef(false);
  const nativeJoinedRef = useRef(false);
  const nativeDropsRef = useRef(0);
  // Safety net: if the native screen never even JOINS the room (crash / permission issue), fall back to the in-page call.
  // Waiting for the callee to answer is not a failure: the native screen ends the call itself as "no answer" after the ring time.
  useEffect(() => {
    if (!nativeMode || phase === 'connected') return undefined;
    nativeJoinedRef.current = false;
    const t = setTimeout(() => {
      if (nativeJoinedRef.current) return;
      nativeFailedRef.current = true; setNativeMode(false); connectingRef.current = false;
      try { nativeBridge && nativeBridge.endNativeCall(); } catch (e) { /* noop */ }
      toast.warning('Native call screen did not connect — continuing in the app');
      setNativeRetry((n) => n + 1);
    }, 30000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeMode, phase]);
  useEffect(() => {
    if (!nativeBridge) return undefined;
    const h = (e) => {
      const d = e.detail || {};
      if (d.call_id && callId && d.call_id !== callId) return;
      if (d.kind === 'screen_share') setIsScreenSharing(d.value === true || d.value === 'true');
      if (d.kind === 'joined') { nativeJoinedRef.current = true; setRoomState('joined'); }
      else if (d.kind === 'connected') { nativeJoinedRef.current = true; markConnected(); setRoomState('joined'); }
      else if (d.kind === 'ended') {
        if (d.value === 'connect_failed' || d.value === 'not_livekit') {
          nativeFailedRef.current = true; setNativeMode(false); connectingRef.current = false; setNativeRetry((n) => n + 1);
          if (d.value === 'connect_failed') { toast.warning('Media server unreachable — switching to direct connection'); fetch(`${API}/api/calls/${callId}/media-mode`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ mode: 'mesh' }) }).catch(() => {}); }
          setMediaMode('mesh');
        } else if (d.value === 'no_answer') { setNativeMode(false); toast.info(`${recipientName || 'The receiver'} did not answer`); endCall('no_answer'); }
        else if (d.value === 'ended_all') { setNativeMode(false); endCall('hung_up', true); }
        else if (d.value === 'disconnected' && nativeDropsRef.current < 2) {
          // Media link dropped (network switch, OS pressure) — re-open the native screen instead of hanging up on everyone.
          nativeDropsRef.current += 1; connectingRef.current = false;
          toast.info('Connection lost — rejoining the call…');
          setTimeout(() => setNativeRetry((n) => n + 1), 800);
        }
        else { setNativeMode(false); endCall('hung_up'); }
      }
    };
    window.addEventListener('bbc:native-call', h);
    return () => window.removeEventListener('bbc:native-call', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeBridge, callId, token]);

  useEffect(() => {
    if (!callId || callId === 'pending' || !token || connectingRef.current || roomRef.current) return undefined;
    if (nativeBridge && !nativeFailedRef.current) {
      connectingRef.current = true;
      setNativeMode(true);
      setRoomState('joined');
      try {
        nativeBridge.openNativeCall(JSON.stringify({ call_id: callId, token, api: API, call_type: callType, outgoing: !!callState.isInitiator, title: recipientName ? `${callType === 'video' ? 'Video call with ' : 'Call with '}${recipientName}` : (callType === 'video' ? 'Video call' : 'Call'),
          is_host: !!callState.isInitiator || (auth.user && auth.user.role === 'admin'), user_id: auth.user && auth.user.user_id }));
      } catch (e) { nativeFailedRef.current = true; setNativeMode(false); connectingRef.current = false; }
      return () => { connectingRef.current = false; };
    }
    connectingRef.current = true;
    let cancelled = false;

    (async () => {
      const res = await fetch(`${API}/api/calls/${callId}/livekit-token`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = res.ok ? await res.json() : { mode: 'mesh' };
      if (cancelled) return;
      if (data.mode !== 'livekit') { setMediaMode('mesh'); return; }
      // TURN relay (coturn) for phones on carrier NAT: without it ICE to the SFU never completes on some
      // mobile networks and the other side stays on "Connecting…" forever.
      let iceServers;
      try {
        const ir = await fetch(`${API}/api/calls/ice-servers`, { headers: { Authorization: `Bearer ${token}` } });
        if (ir.ok) iceServers = (await ir.json()).iceServers;
      } catch (e) { /* use LiveKit defaults */ }
      if (cancelled) return;

      const room = new Room({
        ...(iceServers && iceServers.length ? { rtcConfig: { iceServers, iceTransportPolicy: 'all' } } : {}),
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        videoCaptureDefaults: { resolution: VideoPresets.h540.resolution, facingMode: 'user' },
        publishDefaults: { simulcast: true, videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360], dtx: true, red: true },
      });
      roomRef.current = room;

      const upsertTrack = (track, pub, p) => {
        if (!track || !p) return;
        if (track.kind === Track.Kind.Video) {
          if (pub.source === Track.Source.ScreenShare) patchTile(p.identity, { name: nameFor(p), screen: track });
          else patchTile(p.identity, { name: nameFor(p), video: track, camMuted: !!pub.isMuted });
        } else patchTile(p.identity, { name: nameFor(p), audio: track, micMuted: !!pub.isMuted });
      };
      room
        .on(RoomEvent.ParticipantConnected, (p) => {
          patchTile(p.identity, { name: nameFor(p) }); markConnected(); connectedOnceRef.current = true; clearTimeout(aloneTimerRef.current);
          // late joiners don't know about hands already up: re-announce ours
          if (handRaisedRef.current) room.localParticipant.publishData(enc({ type: 'hand', raised: true, name: auth.user && auth.user.name }), { reliable: true, topic: DATA_TOPIC }).catch(() => {});
        })
        .on(RoomEvent.ParticipantDisconnected, (p) => {
          setTiles((prev) => { const n = { ...prev }; delete n[p.identity]; return n; });
          // Safety net: in a 1-to-1 call, if the other side is gone for 20 s and the server never said "ended"
          // (their page was paused/killed), finish the call here instead of sitting on "waiting for…".
          clearTimeout(aloneTimerRef.current);
          aloneTimerRef.current = setTimeout(() => {
            const r = roomRef.current;
            if (r && r.remoteParticipants.size === 0 && (callStateRef.current.participants || []).length <= 1 && connectedOnceRef.current) {
              toast.info(`${recipientName || 'The other party'} left the call`); leaveRoom(); endCall('hung_up');
            }
          }, 20000);
        })
        .on(RoomEvent.DataReceived, (payload, p, _kind, topic) => {
          if (topic && topic !== DATA_TOPIC) return;
          let msg; try { msg = JSON.parse(new TextDecoder().decode(payload)); } catch (e) { return; }
          if (msg.type === 'hand' && p) {
            patchTile(p.identity, { name: nameFor(p), hand: !!msg.raised });
            if (msg.raised) toast(`✋ ${nameFor(p) || 'A participant'} raised a hand`, { id: `hand-${p.identity}`, duration: 5000 });
          } else if (msg.type === 'lower_all') {
            setTiles((prev) => { const n = {}; Object.keys(prev).forEach((k) => { n[k] = { ...prev[k], hand: false }; }); return n; });
            handRaisedRef.current = false; setHandRaised(false);
          }
        })
        .on(RoomEvent.TrackSubscribed, (track, pub, p) => { upsertTrack(track, pub, p); markConnected(); })
        .on(RoomEvent.TrackUnsubscribed, (track, pub, p) => {
          if (track.kind === Track.Kind.Video) patchTile(p.identity, pub.source === Track.Source.ScreenShare ? { screen: null } : { video: null });
          else patchTile(p.identity, { audio: null });
        })
        .on(RoomEvent.TrackMuted, (pub, p) => { if (p === room.localParticipant) return; if (pub.source === Track.Source.Camera) patchTile(p.identity, { camMuted: true }); else if (pub.source === Track.Source.Microphone) patchTile(p.identity, { micMuted: true }); })
        .on(RoomEvent.TrackUnmuted, (pub, p) => { if (p === room.localParticipant) return; if (pub.source === Track.Source.Camera) patchTile(p.identity, { camMuted: false }); else if (pub.source === Track.Source.Microphone) patchTile(p.identity, { micMuted: false }); })
        .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          const set = new Set(speakers.map((s) => s.identity));
          setMeSpeaking(set.has(room.localParticipant.identity));
          setTiles((prev) => { const n = {}; Object.keys(prev).forEach((k) => { n[k] = { ...prev[k], speaking: set.has(k) }; }); return n; });
        })
        .on(RoomEvent.ConnectionQualityChanged, (q, p) => {
          const level = q === ConnectionQuality.Excellent ? 'excellent' : q === ConnectionQuality.Good ? 'good' : q === ConnectionQuality.Poor ? 'poor' : q === ConnectionQuality.Lost ? 'lost' : 'checking';
          if (p === room.localParticipant) setQuality(level); else patchTile(p.identity, { quality: level });
        })
        .on(RoomEvent.Reconnecting, () => setRoomState('reconnecting'))
        .on(RoomEvent.Reconnected, () => setRoomState('joined'))
        .on(RoomEvent.Disconnected, (reason) => {
          if (reason === DisconnectReason.CLIENT_INITIATED) return;
          setRoomState('failed');
          toast.error('Media server connection lost');
        })
        .on(RoomEvent.AudioPlaybackStatusChanged, () => setNeedsAudioTap(!room.canPlaybackAudio))
        .on(RoomEvent.MediaDevicesError, (e) => setMediaError(e && e.name === 'NotAllowedError' ? 'Camera/Microphone permission denied.' : `Media error: ${(e && e.message) || 'unknown'}`))
        .on(RoomEvent.LocalTrackPublished, (pub) => {
          if (pub.source === Track.Source.ScreenShare) setIsScreenSharing(true);
          attachLocalPreview(room);
        })
        .on(RoomEvent.LocalTrackUnpublished, (pub) => {
          if (pub.source === Track.Source.ScreenShare) { setIsScreenSharing(false); setTimeout(() => attachLocalPreview(room), 0); }
        });

      try {
        await room.connect(data.url, data.token);
        if (cancelled) { room.disconnect(); return; }
        setRoomState('joined');
        room.remoteParticipants.forEach((p) => {
          patchTile(p.identity, { name: nameFor(p) });
          p.trackPublications.forEach((pub) => { if (pub.track) upsertTrack(pub.track, pub, p); });
        });
        if (room.remoteParticipants.size > 0) markConnected();
        try { await room.localParticipant.setMicrophoneEnabled(true); } catch (e) { setMediaError('Microphone unavailable: ' + (e.message || e.name)); }
        if (callType === 'video') {
          try { await room.localParticipant.setCameraEnabled(true); } catch (e) { setIsVideoEnabled(false); toast.warning('Camera unavailable — joined with audio only'); }
        }
        setNeedsAudioTap(!room.canPlaybackAudio);
      } catch (e) {
        console.error('[LiveKitCall] connect failed', e);
        // Media server unreachable from this network → fall back to the direct (mesh) call path
        toast.warning('Media server unreachable — switching to direct connection');
        try { room.disconnect(); } catch (err) { /* noop */ }
        fetch(`${API}/api/calls/${callId}/media-mode`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ mode: 'mesh' }) }).catch(() => {});
        setMediaMode('mesh');
      }
    })().catch((e) => { console.error('[LiveKitCall]', e); setRoomState('failed'); });

    return () => { cancelled = true; connectingRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, token, nativeRetry]);

  // ==================== CLEANUP / BACKEND EVENTS ====================
  const leaveRoom = useCallback(() => {
    const a = statsAccRef.current;
    if (a.n > 0 && callId && callId !== 'pending') {
      fetch(`${API}/api/calls/${callId}/stats`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode: 'livekit', avg_kbps: a.kbps / a.n, avg_loss_pct: a.loss / a.n, samples: a.n }), keepalive: true }).catch(() => {});
      statsAccRef.current = { kbps: 0, loss: 0, n: 0 };
    }
    clearTimeout(aloneTimerRef.current);
    const r = roomRef.current; roomRef.current = null;
    if (r) { try { r.disconnect(); } catch (e) { /* noop */ } }
    if (nativeBridge) { try { nativeBridge.endNativeCall(); } catch (e) { /* noop */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const u1 = registerHandler('callEnded', leaveRoom);
    const u2 = registerHandler('participantLeft', (d) => { if (d && d.user_id) toast.info(`${d.name || 'A participant'} left the call`); });
    const u3 = registerHandler('webrtcSignal', () => { /* SFU mode: no P2P signalling */ });
    return () => { u1(); u2(); u3(); };
  }, [registerHandler, leaveRoom]);

  // Unmount (route change, layout re-render, page reload) must never hang up: only End / the server may. Just drop
  // our in-page room; a native call screen keeps running on its own and closes via FCM call_ended or its End button.
  useEffect(() => () => {
    clearTimeout(aloneTimerRef.current);
    const r = roomRef.current; roomRef.current = null;
    if (r) { try { r.disconnect(); } catch (e) { /* noop */ } }
  }, []);

  useEffect(() => {
    if (callState.leftPeer && participants.length <= 1 && phase === 'connected') {
      toast.info('Everyone has left the call');
      leaveRoom(); endCall('hung_up');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState.leftAt]);

  useEffect(() => {
    if (connectionState !== 'connected') return undefined;
    const t = setInterval(() => setCallDuration((d) => d + 1), 1000);
    return () => clearInterval(t);
  }, [connectionState]);

  useEffect(() => { attachLocalPreview(roomRef.current); }, [minimized, isVideoEnabled, isScreenSharing, attachLocalPreview]);

  // ==================== CONTROLS ====================
  const lp = () => roomRef.current && roomRef.current.localParticipant;
  useEffect(() => {
    const h = (e) => { const d = e.detail || {}; if (d.user_id !== (auth.user && auth.user.user_id)) return;
      if (nativeBridge && typeof nativeBridge.setNativeMic === 'function') { try { nativeBridge.setNativeMic(!d.muted, d.by_name || 'The host'); } catch (err) { /* noop */ } return; }
      const p = lp(); if (!p) return;
      p.setMicrophoneEnabled(!d.muted).then(() => { setIsAudioEnabled(!d.muted); toast.info(`${d.by_name || 'The host'} ${d.muted ? 'muted' : 'unmuted'} your microphone`); }).catch(() => {}); };
    window.addEventListener('bbc:force-mute', h); return () => window.removeEventListener('bbc:force-mute', h);
  }, [auth.user]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleAudio = () => { const p = lp(); if (!p) return; p.setMicrophoneEnabled(!isAudioEnabled).then(() => setIsAudioEnabled(!isAudioEnabled)).catch(() => {}); };
  const canManage = !!callState.isInitiator || (auth.user && auth.user.role === 'admin');
  const toggleHand = () => {
    const p = lp(); if (!p) return;
    const next = !handRaisedRef.current; handRaisedRef.current = next; setHandRaised(next);
    p.publishData(enc({ type: 'hand', raised: next, name: auth.user && auth.user.name }), { reliable: true, topic: DATA_TOPIC }).catch(() => {});
    toast(next ? '✋ Hand raised' : 'Hand lowered', { duration: 2000 });
  };
  const lowerAllHands = () => {
    const p = lp(); if (!p) return;
    p.publishData(enc({ type: 'lower_all' }), { reliable: true, topic: DATA_TOPIC }).catch(() => {});
    setTiles((prev) => { const n = {}; Object.keys(prev).forEach((k) => { n[k] = { ...prev[k], hand: false }; }); return n; });
    handRaisedRef.current = false; setHandRaised(false);
  };
  const hostMute = (uid, name, muted) => fetch(`${API}/api/calls/${callId}/mute/${uid}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ muted }) })
    .then((r) => { if (!r.ok) throw new Error('Could not change mute'); toast.success(`${name || 'Member'} ${muted ? 'muted' : 'unmuted'}`); }).catch((e) => toast.error(e.message));

  // Screen lock / app switch on phones ends or mutes the captured tracks; once
  // we're visible again, restart any publication the user still wants live.
  const restoreLocalTracks = useCallback(() => {
    setTimeout(async () => {
      const p = lp(); if (!p) return;
      let restored = false;
      for (const source of [Track.Source.Microphone, Track.Source.Camera]) {
        const pub = p.getTrackPublication(source);
        const t = pub && pub.track;
        if (!t || pub.isMuted) continue;
        const mst = t.mediaStreamTrack;
        if (mst && mst.readyState !== 'ended' && !mst.muted) continue;
        try { await t.restartTrack(); restored = true; } catch (e) { console.warn('[LiveKitCall] restart failed', source, e); }
      }
      if (restored) toast.success('Microphone restored');
    }, 1200);
  }, []);
  useCallKeepAlive(!!roomRef.current || phase === 'connected' || phase === 'connecting' || phase === 'outgoing_ringing', restoreLocalTracks,
    { title: recipientName ? `${callType === 'video' ? 'Video call with ' : 'Call with '}${recipientName}` : '', video: callType === 'video' });
  const [facing, setFacing] = useState('user');
  const flipCamera = async () => {
    const p = lp(); const pub = p && p.getTrackPublication(Track.Source.Camera);
    if (!pub || !pub.videoTrack) return;
    const next = facing === 'user' ? 'environment' : 'user';
    try { await pub.videoTrack.restartTrack({ facingMode: next }); setFacing(next); } catch (e) { toast.error('Could not switch camera'); }
  };
  const toggleVideo = () => { const p = lp(); if (!p) return; p.setCameraEnabled(!isVideoEnabled).then(() => setIsVideoEnabled(!isVideoEnabled)).catch(() => toast.error('Could not switch camera')); };
  const toggleScreenShare = () => {
    const p = lp(); if (!p) return;
    if (!isScreenSharing && !(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia)) {
      toast.error('Screen sharing is not supported on this device', { description: 'Use the app in a desktop browser (Chrome/Edge) to share your screen.' });
      return;
    }
    p.setScreenShareEnabled(!isScreenSharing, { audio: false, resolution: VideoPresets.h720.resolution })
      .catch((e) => { if (e.name !== 'NotAllowedError' && e.name !== 'AbortError') toast.error('Could not start screen sharing'); });
  };
  const toggleFullscreen = () => {
    if (!isFullscreen) { if (containerRef.current && containerRef.current.requestFullscreen) containerRef.current.requestFullscreen(); }
    else if (document.exitFullscreen) document.exitFullscreen();
    setIsFullscreen(!isFullscreen);
  };
  const handleEndCall = () => { leaveRoom(); endCall('hung_up'); };
  // Group call: End = leave; the host can long-press (600 ms) to end the call for everyone.
  const isGroupCall = participants.length >= 2;
  const endPressRef = useRef({ t: null, fired: false });
  const handleEndForAll = async () => {
    if (!(await confirmDialog({ title: 'End the call for everyone?', description: `${participants.length + 1} people are in this call. Everyone will be disconnected.`, confirmText: 'End for everyone', variant: 'danger' }))) return;
    leaveRoom(); endCall('hung_up', true);
  };
  const endPressStart = () => {
    endPressRef.current.fired = false;
    if (!(callState.isInitiator && isGroupCall)) return;
    endPressRef.current.t = setTimeout(() => { endPressRef.current.fired = true; handleEndForAll(); }, 600);
  };
  const endPressStop = () => { clearTimeout(endPressRef.current.t); endPressRef.current.t = null; };
  const endPressClick = () => { if (endPressRef.current.fired) { endPressRef.current.fired = false; return; } handleEndCall(); };
  const enableAudio = () => { const r = roomRef.current; if (r) r.startAudio().then(() => setNeedsAudioTap(false)).catch(() => {}); };

  // ==================== ADD PARTICIPANTS ====================
  useEffect(() => {
    if (!addOpen) return undefined;
    const t = setTimeout(() => {
      fetch(`${API}/api/chat/users/search?q=${encodeURIComponent(addQuery.trim() || 'a')}&limit=30`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => {
          const list = Array.isArray(d) ? d : (d.users || d.results || []);
          const inCall = { [myId]: true }; participants.forEach((p) => { inCall[p.user_id] = true; }); Object.keys(tiles).forEach((k) => { inCall[k] = true; });
          setAddResults(list.filter((u) => u.user_id && !inCall[u.user_id]).slice(0, 20));
        }).catch(() => setAddResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [addOpen, addQuery, token, participants, myId, tiles]);

  const invite = (u) => { inviteToCall([u.user_id]); toast.success(`Ringing ${u.name || 'participant'}…`); setAddOpen(false); setAddQuery(''); };

  // ==================== PILL DRAG ====================
  const onPillDown = (e) => {
    const evt = e.touches ? e.touches[0] : e;
    const rect = e.currentTarget.getBoundingClientRect();
    pillDragRef.current = { active: true, moved: false, offX: evt.clientX - rect.left, offY: evt.clientY - rect.top, w: rect.width, h: rect.height };
    const onMove = (ev) => {
      const d = pillDragRef.current; if (!d.active) return; d.moved = true;
      const m = ev.touches ? ev.touches[0] : ev;
      setPillPos({ x: Math.max(4, Math.min(m.clientX - d.offX, window.innerWidth - d.w - 4)), y: Math.max(4, Math.min(m.clientY - d.offY, window.innerHeight - d.h - 4)) });
      if (ev.cancelable && ev.type === 'touchmove') ev.preventDefault();
    };
    const onUp = () => {
      const d = pillDragRef.current; d.active = false;
      window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp);
      if (d.moved) setPillPos((p) => { try { localStorage.setItem('bbc:callPillPos', JSON.stringify(p)); } catch (e) { /* quota */ } return p; });
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onUp);
  };

  // ==================== RENDER ====================
  const q = QUALITY_UI[quality] || QUALITY_UI.checking; const QIcon = q.icon;
  let tileList = [];
  Object.keys(tiles).forEach((k) => {
    tileList.push({ key: k, identity: k, info: tiles[k], kind: 'camera' });
    if (tiles[k].screen) tileList.push({ key: `${k}#screen`, identity: k, info: tiles[k], kind: 'screen' });
  });
  // A shared screen owns the whole stage (no camera tiles beside it, no self-preview on top of it).
  const remoteScreen = tileList.some((t) => t.kind === 'screen');
  if (remoteScreen) tileList = tileList.filter((t) => t.kind === 'screen');
  const isGroup = remoteCount >= 2;
  // Group call: you are a card too (mic / hand state), so everyone sees the same roster.
  if (isGroup && !remoteScreen) tileList.unshift({ key: 'me', identity: myId, kind: 'camera', self: true, info: { name: `${(auth.user && auth.user.name) || 'You'} (you)`, camMuted: true, micMuted: !isAudioEnabled, hand: handRaised, speaking: meSpeaking } });
  const speakingNow = Object.values(tiles).filter((t) => t.speaking).map((t) => t.name || 'Someone').concat(meSpeaking ? ['You'] : []);
  const handsUp = Object.values(tiles).filter((t) => t.hand).length + (handRaised ? 1 : 0);
  const n = tileList.length;
  const gridCols = n <= 1 ? 'grid-cols-1' : n <= 4 ? 'grid-cols-2' : n <= 9 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-3 sm:grid-cols-4';
  const statusLabel = connectionState === 'connected' ? 'Connected' : connectionState === 'failed' ? 'Failed' : roomState === 'reconnecting' ? 'Reconnecting…' : phase === 'outgoing_ringing' ? (recipientName ? `Calling ${recipientName.split(' ').slice(0, 2).join(' ')}…` : 'Calling…') : 'Connecting…';

  // Native app: no web "Calling…" screen while the server creates the call — the native call screen is the only screen.
  if (nativeBridge && !nativeFailedRef.current && !nativeMode && (callId === 'pending' || !callId)) {
    return (
      <div className="fixed inset-0 z-[60] bg-gray-950 flex flex-col items-center justify-center text-white" data-testid="native-call-starting">
        <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">{callType === 'video' ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}</div>
        <p className="text-lg font-medium">{recipientName || 'Starting call'}</p>
        <p className="text-sm text-gray-400 mt-1">Calling…</p>
        <Button variant="ghost" size="icon" onClick={handleEndCall} className="mt-10 w-14 h-14 rounded-full bg-red-500 text-white hover:bg-red-600" data-testid="native-call-starting-end"><PhoneOff className="w-6 h-6" /></Button>
      </div>
    );
  }

  if (nativeMode) {
    // panel stays mounted (closed) so the AI-notes switch keeps reaching the native call screen
    const notesRelay = <MeetingNotesPanel callId={callId} token={token} isHost={canManage} open={false} onClose={() => {}} onStatus={setAiOn} getMicTrack={() => null} />;
    const back = () => { try { nativeBridge.openNativeCall(JSON.stringify({ call_id: callId, token, api: API, call_type: callType, title: recipientName || '', is_host: !!callState.isInitiator || (auth.user && auth.user.role === 'admin'), user_id: auth.user && auth.user.user_id })); } catch (e) { /* noop */ } };
    return (
      <>{notesRelay}<div className="fixed z-[60] touch-none select-none" style={bubblePos ? { left: bubblePos.x, top: bubblePos.y } : { bottom: 20, right: 20 }} data-testid="native-call-banner"
        onPointerDown={(e) => { const r = e.currentTarget.getBoundingClientRect(); bubbleDrag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width, h: r.height, moved: false, id: e.pointerId }; e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { const d = bubbleDrag.current; if (!d || d.id !== e.pointerId) return; const x = Math.min(Math.max(0, e.clientX - d.dx), window.innerWidth - d.w); const y = Math.min(Math.max(0, e.clientY - d.dy), window.innerHeight - d.h); if (!d.moved && Math.abs(e.clientX - (d.startX ?? e.clientX)) + Math.abs(e.clientY - (d.startY ?? e.clientY)) < 4) { d.startX = d.startX ?? e.clientX; d.startY = d.startY ?? e.clientY; return; } d.moved = true; setBubblePos({ x, y }); }}
        onPointerUp={() => { const d = bubbleDrag.current; bubbleDrag.current = null; bubbleJustMoved.current = !!(d && d.moved); if (bubbleJustMoved.current) setTimeout(() => { bubbleJustMoved.current = false; }, 250); }}
        onClickCapture={(e) => { if (bubbleJustMoved.current) { e.stopPropagation(); e.preventDefault(); } }}>
        <div className="flex items-center gap-2 p-1.5 rounded-full bg-gray-900/95 text-white shadow-2xl border border-white/10 cursor-grab active:cursor-grabbing">
          <button type="button" onClick={back} className="relative flex w-10 h-10 rounded-full bg-green-600 items-center justify-center" title="Return to call" data-testid="native-call-return">
            {callType === 'video' ? <Video className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
            {phase === 'connected' && <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-40 animate-ping" />}
          </button>
          {isScreenSharing && (
            <button type="button" onClick={back} className="flex items-center gap-1 pl-1 pr-2 h-8 rounded-full bg-blue-600 text-[11px] font-medium whitespace-nowrap" title="You are sharing your screen — tap to return" data-testid="native-call-sharing-badge">
              <Monitor className="w-3.5 h-3.5" /> Sharing screen
            </button>
          )}
          <Button variant="ghost" size="icon" onClick={() => { const next = !bubbleMuted; setBubbleMuted(next); try { nativeBridge.setNativeMic(!next, 'You'); } catch (e) { /* noop */ } }} className={`w-10 h-10 rounded-full text-white ${bubbleMuted ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gray-600 hover:bg-gray-700'}`} title={bubbleMuted ? 'Unmute' : 'Mute'} data-testid="native-call-bubble-mute">{bubbleMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}</Button>
          <Button variant="ghost" size="icon" onClick={handleEndCall} className="w-10 h-10 rounded-full bg-red-500 text-white hover:bg-red-600" title="End call" data-testid="native-call-end"><PhoneOff className="w-4 h-4" /></Button>
        </div>
      </div></>
    );
  }

  return (
    <>
      {minimized && (
        <div className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 pl-2 pr-2 py-2 rounded-full bg-gray-900 text-white shadow-2xl border border-white/10 cursor-grab active:cursor-grabbing touch-none select-none animate-in fade-in zoom-in-95"
          data-testid="call-pill" onMouseDown={onPillDown} onTouchStart={onPillDown}
          style={pillPos ? { left: pillPos.x, top: pillPos.y, right: 'auto', bottom: 'auto' } : undefined}>
          <button type="button" onClick={() => { if (!pillDragRef.current.moved) setMinimized(false); }} className="flex items-center gap-2 pl-1" data-testid="call-pill-restore">
            <span className="relative flex w-9 h-9 rounded-full bg-green-600 items-center justify-center">
              {callType === 'video' ? <Video className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
              {connectionState === 'connected' && <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-50 animate-ping" />}
            </span>
            <span className="text-left leading-tight pr-1">
              <span className="block text-xs font-medium">{connectionState === 'connected' ? `Ongoing call${remoteCount > 1 ? ` · ${remoteCount + 1}` : ''}` : statusLabel}</span>
              <span className="block text-[11px] font-mono text-green-300">{connectionState === 'connected' ? fmt(callDuration) : 'Connecting…'}</span>
            </span>
          </button>
          <Button variant="ghost" size="icon" onClick={handleEndCall} className="w-9 h-9 rounded-full bg-red-500 text-white hover:bg-red-600" data-testid="call-pill-end"><PhoneOff className="w-4 h-4" /></Button>
        </div>
      )}

      <div ref={containerRef} style={{ display: minimized ? 'none' : 'flex' }} className="fixed inset-0 bg-gray-900 z-50 flex-col" data-testid="video-call" data-engine="livekit">
        {Object.keys(tiles).map((k) => <TrackAudio key={`a${k}`} track={tiles[k].audio} />)}

        {controlsHidden && (
          <>
            {/* Tap anywhere on the stage to bring the bars back (Android status bar can cover the top corner) */}
            <button type="button" aria-label="Show controls" onClick={() => setControlsHidden(false)} className="absolute inset-0 z-10 bg-transparent" data-testid="show-controls-stage" />
            <button type="button" onClick={() => setControlsHidden(false)}
              style={{ top: 'max(env(safe-area-inset-top, 0px) + 12px, 56px)' }}
              className="absolute right-3 z-20 h-11 px-3 rounded-full bg-black/70 text-white flex items-center gap-2 backdrop-blur shadow-lg border border-white/30" title="Show controls" data-testid="show-controls-btn">
              <Eye className="w-5 h-5" /><span className="text-xs font-medium">Show</span>
            </button>
          </>
        )}
        <div ref={topBarRef} style={{ display: controlsHidden ? 'none' : undefined }} className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex items-center justify-between z-10 bg-gray-900/95">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Badge variant={connectionState === 'connected' ? 'default' : 'secondary'} data-testid="call-connection-badge">{statusLabel}</Badge>
            <Badge variant="outline" className="border-white/30 text-sky-300 gap-1" title="Routed through the BBC media server (SFU)" data-testid="call-path-badge"><Server className="w-3 h-3" /> Server</Badge>
            {connectionState === 'connected' ? <span className="text-white font-mono text-sm" data-testid="call-timer">{fmt(callDuration)}</span> : <span className="text-white/70 text-xs animate-pulse" data-testid="call-connecting">{statusLabel}</span>}
            {remoteCount > 0 && <span className="text-white/70 text-xs hidden sm:inline" data-testid="call-participant-count">{remoteCount + 1} in call</span>}
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {connectionState === 'connected' && (
              <div className="relative">
                <Button variant="ghost" size="sm" onClick={() => setShowQualityDetails(!showQualityDetails)} className={`text-white hover:bg-white/20 gap-1 px-2 ${q.color}`} data-testid="call-quality-btn">
                  <QIcon className="w-5 h-5" /><span className="text-xs hidden sm:inline">{q.label}</span>
                </Button>
                {showQualityDetails && (
                  <div className="absolute right-0 top-full mt-1 bg-gray-800/95 backdrop-blur rounded-lg p-3 min-w-[210px] text-white text-xs space-y-1 shadow-xl border border-gray-700" data-testid="call-quality-details">
                    <div className="font-semibold text-sm mb-2">Quality: {q.label}</div>
                    <div className="flex justify-between"><span className="text-gray-400">Path:</span><span>Media server (SFU)</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Participants:</span><span>{remoteCount + 1}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Adaptive video:</span><span>On</span></div>
                    <div className="mt-2 pt-2 border-t border-gray-700 space-y-1" data-testid="call-stats-list">
                      <div className="flex justify-between"><span className="text-gray-400">You (sending):</span><span data-testid="call-stats-me">{fmtStats(stats.me)}</span></div>
                      {Object.keys(tiles).map((id) => <div key={id} className="flex justify-between"><span className="text-gray-400 truncate mr-2">{tiles[id].name || id}:</span><span>{fmtStats(stats[id])}</span></div>)}
                    </div>
                    {remoteCount <= 1 && (
                      <button type="button" data-testid="switch-direct-btn" className="mt-2 w-full rounded-md bg-white/15 hover:bg-white/25 px-2 py-1.5 text-left"
                        onClick={async () => {
                          try {
                            const r = await fetch(`${API}/api/calls/${callId}/media-mode`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ mode: 'mesh' }) });
                            if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
                            toast.success('Switching to direct connection…'); setShowQualityDetails(false); setMediaMode('mesh');
                          } catch (e) { toast.error(e.message); }
                        }}>
                        <span className="block font-medium">Direct connection (1:1)</span>
                        <span className="block text-[10px] text-gray-300">Phone-to-phone, lowest latency. Best when both are on good Wi-Fi.</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={() => setNotesOpen((v) => !v)} className={`text-white hover:bg-white/20 relative ${notesOpen ? 'bg-white/20' : ''}`} title="Meeting notes" data-testid="meeting-notes-btn"><NotebookPen className="w-5 h-5" />{aiOn && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-testid="ai-notes-dot" />}</Button>
            <Button variant="ghost" size="icon" onClick={() => setAddOpen(true)} className="text-white hover:bg-white/20" title="Add participant" data-testid="add-participant-btn"><UserPlus className="w-5 h-5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setControlsHidden(true)} className="text-white hover:bg-white/20" title="Hide controls (full scene)" data-testid="hide-controls-btn"><EyeOff className="w-5 h-5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setMinimized(true)} className="text-white hover:bg-white/20" data-testid="minimise-call-btn"><Minus className="w-5 h-5" /></Button>
            <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-white hover:bg-white/20 hidden sm:inline-flex" data-testid="fullscreen-btn">{isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</Button>
          </div>
        </div>

        {connectionState === 'connected' && isGroup && speakingNow.length > 0 && !remoteScreen && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-green-600/95 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow" data-testid="speaking-now-strip">
            <Mic className="w-3.5 h-3.5 animate-pulse" /> {speakingNow.slice(0, 2).join(' & ')}{speakingNow.length > 2 ? ` +${speakingNow.length - 2}` : ''} speaking
          </div>
        )}
        {connectionState === 'connected' && !(isGroup && speakingNow.length > 0) && (quality === 'poor' || quality === 'lost') && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-yellow-500/95 text-black text-xs font-medium px-3 py-1.5 rounded-full shadow" data-testid="weak-signal-banner">
            <SignalLow className="w-4 h-4" /> {quality === 'lost' ? 'Your connection dropped — reconnecting…' : 'Weak signal on your side — move closer to a window or switch to Wi-Fi'}
          </div>
        )}
        {connectionState === 'connected' && quality !== 'poor' && quality !== 'lost' && Object.values(tiles).some((t) => t.quality === 'poor' || t.quality === 'lost') && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full" data-testid="peer-weak-signal-banner">
            {Object.values(tiles).filter((t) => t.quality === 'poor' || t.quality === 'lost').map((t) => t.name || 'Participant').join(', ')} has a weak signal
          </div>
        )}

        <CallExtras callId={callId} token={token} isInitiator={!!callState.isInitiator} isAdmin={auth.user && auth.user.role === 'admin'} myId={auth.user && auth.user.user_id}
          members={Object.keys(tiles).map((id) => ({ user_id: id, name: tiles[id].name }))} />
        <MeetingNotesPanel callId={callId} token={token} isHost={canManage} open={notesOpen} onClose={() => setNotesOpen(false)} onStatus={setAiOn}
          getMicTrack={() => { const p = lp(); const pub = p && p.getTrackPublication(Track.Source.Microphone); return pub && pub.track ? pub.track.mediaStreamTrack : null; }} />
        {mediaError && <div className="absolute top-16 left-4 right-4 z-20 bg-red-600/90 text-white p-3 rounded-lg text-sm">{mediaError}</div>}
        {needsAudioTap && connectionState === 'connected' && (
          <button type="button" onClick={enableAudio} className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-amber-500 text-black font-medium px-4 py-2 rounded-full shadow-lg" data-testid="enable-audio-btn">
            <Volume2 className="w-4 h-4" /> Tap to hear the call
          </button>
        )}

        <div className="flex-1 relative overflow-hidden" style={{ paddingTop: barPad.top, paddingBottom: barPad.bottom }} data-testid="call-stage">
          {n > 0 ? (
            <div className={`grid gap-1 h-full w-full ${remoteScreen ? 'p-0 auto-rows-[1fr]' : 'p-1 overflow-y-auto auto-rows-[minmax(110px,1fr)]'} ${gridCols}`} data-testid="remote-video-grid">
              {tileList.map((t) => <PeerTile key={t.key} identity={t.identity} info={t.info} kind={t.kind} stats={stats} self={t.self} onMute={canManage && !t.self && t.kind === 'camera' ? hostMute : null} />)}
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
              <div className="text-center text-white">
                <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
                <p className="text-lg font-medium">{connectionState === 'failed' ? 'Connection Failed' : phase === 'outgoing_ringing' ? (recipientName ? `Calling ${recipientName}…` : 'Calling…') : (recipientName ? `Connecting to ${recipientName}…` : 'Connecting...')}</p>
                <p className={`text-sm mt-1 ${callState.statusNote ? 'text-amber-300 font-medium' : 'text-gray-400'}`} data-testid="call-status-hint">{callState.statusNote ? `${callState.statusNote} — still ringing` : phase === 'outgoing_ringing' ? `Waiting for ${recipientName || 'someone'} to answer` : roomState === 'joined' ? `Waiting for ${recipientName || 'the other side'} to join the media server` : 'Joining the media server'}</p>
              </div>
            </div>
          )}
          {(callType === 'video' || isScreenSharing) && (isVideoEnabled || isScreenSharing) && !remoteScreen && (
            <div className="absolute right-4 w-28 h-20 sm:w-48 sm:h-36 rounded-lg overflow-hidden shadow-xl border-2 border-white/20 z-10 bg-black" style={{ bottom: barPad.bottom + 8 }}>
              <video ref={localVideoRef} autoPlay playsInline muted className={`w-full h-full ${isScreenSharing ? 'object-contain bg-black' : 'object-cover'}`} data-testid="local-video" />
            </div>
          )}
        </div>

        {addOpen && (
          <div className="absolute inset-0 z-30 bg-black/70 flex items-end sm:items-center justify-center p-3" data-testid="add-participant-dialog">
            <div className="bg-white rounded-xl w-full max-w-md p-4 max-h-[75vh] flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-base">Add to call</h3>
                <Button variant="ghost" size="icon" onClick={() => setAddOpen(false)} data-testid="add-participant-close"><X className="w-4 h-4" /></Button>
              </div>
              <Input autoFocus placeholder="Search people by name…" value={addQuery} onChange={(e) => setAddQuery(e.target.value)} data-testid="add-participant-search" />
              <div className="mt-2 overflow-y-auto divide-y">
                {addResults.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No people found</p>}
                {addResults.map((u) => (
                  <button key={u.user_id} type="button" onClick={() => invite(u)} className="w-full text-left py-2.5 px-1 hover:bg-muted rounded flex items-center justify-between" data-testid={`add-participant-${u.user_id}`}>
                    <span className="min-w-0"><span className="block text-sm font-medium truncate">{u.name}</span><span className="block text-xs text-muted-foreground truncate">{u.role || u.email || ''}</span></span>
                    <Phone className="w-4 h-4 text-green-600 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div ref={bottomBarRef} style={{ display: controlsHidden ? 'none' : undefined }} className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex flex-col items-center gap-2 bg-gray-900/95">
          {IS_TOUCH_DEVICE && <p className="text-[11px] text-white/70 text-center max-w-xs" data-testid="lock-screen-hint">{LOCK_HINT}</p>}
          <div className="flex items-center justify-center gap-3 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={toggleAudio} className={`w-12 h-12 rounded-full ${isAudioEnabled ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-red-500 text-white hover:bg-red-600'}`} data-testid="toggle-audio">{isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}</Button>
          {isVideoEnabled && <Button variant="ghost" size="icon" onClick={flipCamera} className="w-12 h-12 rounded-full bg-white/20 text-white hover:bg-white/30" title={facing === 'user' ? 'Switch to back camera' : 'Switch to front camera'} data-testid="flip-camera"><SwitchCamera className="w-5 h-5" /></Button>}
          <Button variant="ghost" size="icon" onClick={toggleVideo} className={`w-12 h-12 rounded-full ${isVideoEnabled ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-red-500 text-white hover:bg-red-600'}`} data-testid="toggle-video">{isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}</Button>
          <Button variant="ghost" size="icon" onClick={toggleScreenShare} className={`w-12 h-12 rounded-full ${isScreenSharing ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-white/20 text-white hover:bg-white/30'}`} data-testid="toggle-screen-share"><Monitor className="w-5 h-5" /></Button>
          <Button variant="ghost" size="icon" onClick={toggleHand} className={`w-12 h-12 rounded-full ${handRaised ? 'bg-amber-400 text-black hover:bg-amber-300' : 'bg-white/20 text-white hover:bg-white/30'}`} title={handRaised ? 'Lower hand' : 'Raise hand'} data-testid="toggle-hand"><Hand className="w-5 h-5" /></Button>
          {canManage && handsUp > 0 && (
            <Button variant="ghost" size="sm" onClick={lowerAllHands} className="h-12 rounded-full bg-white/20 text-white hover:bg-white/30 px-3 text-xs" title="Lower all hands" data-testid="lower-all-hands">✋ {handsUp} · lower all</Button>
          )}
          <Button variant="ghost" size="icon" onClick={endPressClick} onPointerDown={endPressStart} onPointerUp={endPressStop} onPointerLeave={endPressStop} onContextMenu={(e) => e.preventDefault()} className="w-14 h-14 rounded-full bg-red-500 text-white hover:bg-red-600 select-none" title={callState.isInitiator && isGroupCall ? 'End call (leave) · long-press: end for everyone' : 'End call'} data-testid="end-call"><PhoneOff className="w-6 h-6" /></Button>
          </div>
        </div>
      </div>
    </>
  );
}

function TrackAudio({ track }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el || !track) return undefined;
    track.attach(el);
    return () => { track.detach(el); };
  }, [track]);
  return <audio ref={ref} autoPlay style={{ display: 'none' }} />;
}

function PeerTile({ identity, info, kind, stats = {}, self = false, onMute = null }) {
  const ref = useRef(null);
  const track = kind === 'screen' ? info.screen : info.video;
  const showVideo = !!track && (kind === 'screen' || !info.camMuted);
  useEffect(() => {
    const el = ref.current; if (!el || !track) return undefined;
    track.attach(el);
    return () => { track.detach(el); };
  }, [track]);
  const speaking = info.speaking && kind === 'camera';
  return (
    <div className={`relative bg-black overflow-hidden transition-shadow ${kind === 'screen' ? 'h-full' : 'rounded-xl min-h-[110px]'} ${speaking ? 'ring-[3px] ring-green-400 shadow-[0_0_18px_rgba(74,222,128,0.6)]' : info.hand ? 'ring-2 ring-amber-400' : ''}`}
      data-testid={self ? 'self-tile' : `remote-tile-${identity}${kind === 'screen' ? '-screen' : ''}`} data-speaking={speaking ? 'true' : 'false'}>
      {!self && <video ref={ref} autoPlay playsInline muted className={`w-full h-full ${kind === 'screen' ? 'object-contain' : 'object-cover'}`} data-testid="remote-video" />}
      {(!showVideo || self) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className={`w-16 h-16 rounded-full text-white flex items-center justify-center text-xl font-semibold transition-transform ${speaking ? 'bg-green-500 scale-110' : self ? 'bg-forest-600' : 'bg-green-700'}`}>{(info.name || '?').trim().charAt(0).toUpperCase()}</div>
        </div>
      )}
      {kind === 'camera' && (
        <div className="absolute top-1.5 left-1.5 right-1.5 flex items-center justify-between gap-1 pointer-events-none">
          <span className="flex items-center gap-1">
            {info.micMuted && <span className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center" title="Muted" data-testid={`tile-muted-${identity}`}><MicOff className="w-3.5 h-3.5" /></span>}
            {speaking && <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center animate-pulse" title="Speaking" data-testid={`tile-speaking-${identity}`}><Mic className="w-3.5 h-3.5" /></span>}
          </span>
          {info.hand && <span className="h-6 px-1.5 rounded-full bg-amber-400 text-black text-xs font-semibold flex items-center gap-1" title="Hand raised" data-testid={`tile-hand-${identity}`}><Hand className="w-3.5 h-3.5" /> hand</span>}
        </div>
      )}
      <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1">
        <span className="text-white text-xs bg-black/50 rounded px-1.5 py-0.5 truncate">{info.name || 'Participant'}{kind === 'screen' ? ' · screen' : ''}</span>
        <span className="flex items-center gap-1">
          {kind === 'camera' && info.quality && (() => { const Q = QUALITY_UI[info.quality] || QUALITY_UI.checking; const I = Q.icon; return <span className={`flex items-center gap-1 text-[10px] bg-black/50 rounded px-1 py-0.5 ${Q.color}`} title={`${info.name || 'Participant'}'s connection: ${Q.label}`} data-testid={`remote-quality-${identity}`}><I className="w-3 h-3" />{(info.quality === 'poor' || info.quality === 'lost') ? Q.label : ''}{stats[identity] ? <span className="text-gray-300 ml-1">{stats[identity].kbps}k · {stats[identity].loss}%</span> : null}</span>; })()}
          {onMute && (
            <button type="button" onClick={() => onMute(identity, info.name, !info.micMuted)} className={`h-6 px-1.5 rounded text-[10px] text-white flex items-center gap-1 ${info.micMuted ? 'bg-white/25 hover:bg-white/40' : 'bg-red-600/90 hover:bg-red-600'}`} title={info.micMuted ? 'Unmute this member' : 'Mute this member'} data-testid={`tile-host-mute-${identity}`}>
              {info.micMuted ? <Mic className="w-3 h-3" /> : <MicOff className="w-3 h-3" />}{info.micMuted ? 'Unmute' : 'Mute'}
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
